import React, { useState } from "react";
import { Button, Spin } from "antd";
import { PlusOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Upload } from "antd";
import { NAVY, FORMAT_COLORS } from "../gateway.constants";

// ── System card ───────────────────────────────────────────────
function SystemCard({ sys, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 6,
        border: `1.5px solid ${selected ? sys.accentColor || NAVY : "var(--color-border-tertiary)"}`,
        background: selected ? `${sys.accentColor || NAVY}12` : "transparent",
        transition: "all .15s",
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 7, flexShrink: 0,
        background: sys.accentColor || NAVY, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 700, fontSize: 11, fontFamily: "monospace",
      }}>
        {sys.logoInitials || sys.systemId.slice(0, 4)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: selected ? 600 : 400,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {sys.displayName}
        </div>
        <div style={{
          fontSize: 11, color: "var(--color-text-secondary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {sys.description}
        </div>
      </div>
    </div>
  );
}

// ── Format card ───────────────────────────────────────────────
function FormatCard({ template, system, selected, onSelect, onPreview }) {
  const fmt = FORMAT_COLORS[template.formatTypeId] || { bg: "#f5f5f5", text: "#666" };
  const isSel = selected?.templateId === template.templateId;
  return (
    <div style={{
      borderRadius: 10, marginBottom: 8,
      border: `1.5px solid ${isSel ? system?.accentColor || NAVY : "var(--color-border-tertiary)"}`,
      background: isSel ? `${system?.accentColor || NAVY}0a` : "var(--color-background-primary)",
      overflow: "hidden", transition: "all .15s",
    }}>
      <div style={{ padding: "10px 12px", cursor: "pointer" }} onClick={() => onSelect(template)}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 13, fontWeight: 500,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {template.templateName}
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {template.dataType}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10,
              background: fmt.bg, color: fmt.text,
            }}>
              {template.formatTypeId?.replace("_", " ")}
            </span>
            {isSel && <CheckCircleOutlined style={{ color: "#3B6D11", fontSize: 12 }} />}
          </div>
        </div>
        {template.description && (
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 5, lineHeight: 1.4 }}>
            {template.description}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
          <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
            {template.fieldCount > 0 ? `${template.fieldCount} fields` : ""}
          </span>
          <Button
            size="small" type="link"
            style={{ fontSize: 11, padding: "0 4px", height: "auto" }}
            onClick={(e) => { e.stopPropagation(); onPreview(template); }}
          >
            Preview
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Left panel (system list + format library + upload) ────────
function SystemSelector({
  systems, selectedSystem, onSelectSystem,
  templates, selectedTemplate, loadingTemplates,
  onSelectTemplate, onPreviewTemplate, onUploadSpec,
}) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    try { await onUploadSpec(file); } finally { setUploading(false); }
    return false;
  };

  return (
    <div style={{
      width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12,
      height: "calc(100vh - 130px)", position: "sticky", top: 0,
    }}>
      {/* System list */}
      <div style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: 12, padding: "14px 12px",
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: ".6px", color: "var(--color-text-secondary)", marginBottom: 10,
        }}>
          Target System
        </div>
        {systems.map((sys) => (
          <SystemCard
            key={sys.systemId} sys={sys}
            selected={selectedSystem?.systemId === sys.systemId}
            onClick={() => onSelectSystem(sys)}
          />
        ))}
      </div>

      {/* Format library */}
      {selectedSystem && (
        <div style={{
          flex: 1, minHeight: 0,
          background: "var(--color-background-primary)",
          border: "0.5px solid var(--color-border-tertiary)",
          borderRadius: 12, padding: "14px 12px",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            fontSize: 11, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: ".6px", color: "var(--color-text-secondary)", marginBottom: 10,
          }}>
            Format Library
          </div>
          <div style={{ flex: 1, overflowY: "auto", marginBottom: 10 }}>
            {loadingTemplates ? (
              <div style={{ textAlign: "center", padding: 20 }}><Spin size="small" /></div>
            ) : templates.length === 0 ? (
              <div style={{
                fontSize: 12, color: "var(--color-text-secondary)",
                textAlign: "center", padding: 16, lineHeight: 1.6,
              }}>
                No formats yet.<br />Upload a spec below.
              </div>
            ) : (
              templates.map((t) => (
                <FormatCard
                  key={t.templateId} template={t} system={selectedSystem}
                  selected={selectedTemplate}
                  onSelect={onSelectTemplate} onPreview={onPreviewTemplate}
                />
              ))
            )}
          </div>
          <div style={{ borderTop: "0.5px solid var(--color-border-tertiary)", paddingTop: 10 }}>
            <Upload
              accept=".xlsx,.xls,.xsd,.zip" maxCount={1}
              beforeUpload={handleUpload} showUploadList={false}
            >
              <Button block icon={<PlusOutlined />} loading={uploading} size="small" style={{ fontSize: 12 }}>
                {uploading ? "Parsing spec…" : "Upload new spec"}
              </Button>
            </Upload>
            <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 5, lineHeight: 1.4 }}>
              .xlsx for CSV/IDX · .xsd or .zip for XML
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemSelector;
